import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useMemberStore } from "../../store/useStore";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Pagination from "../../components/common/Pagination";
import SortDropdown from "../../components/common/SortDropdown";
import SearchBar from "../../components/common/SearchBar";
import Button from "../../components/common/Button";

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #fff8f2 100%);
`;

const ContentContainer = styled.div`
  padding: 32px 40px;
`;

const MemberList = () => {
  const {
    members,
    currentPage,
    itemsPerPage,
    sortBy,
    searchTerm,
    isLoading,
    error,
    setCurrentPage,
    setSortBy,
    setSearchTerm,
    setSearchField,
    fetchMembers,
    getFilteredMembers,
  } = useMemberStore();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  const sortOptions = [
    { value: "recent", label: "최근 가입 순" },
    { value: "old", label: "오래된 가입 순" },
  ];

  const filteredMembers = useMemo(() => getFilteredMembers(), [members]);

  // 서버 페이징: 총 페이지 = Math.ceil(total/itemsPerPage) 대신 커서 기반이므로 prev/next만
  const [totalPages] = useState(9999); // UI 유지용, 실제로는 nextCursor로 이동 제어

  useEffect(() => {
    fetchMembers(1);
  }, [fetchMembers]);

  const columns = [
    {
      header: "No",
      key: "id",
      render: (value, row, index) =>
        (currentPage - 1) * itemsPerPage + index + 1,
    },
    {
      header: "휴대폰 번호",
      key: "phoneNumber",
    },
    {
      header: "회원가입 일",
      key: "joinedAt",
    },
    // 쿠폰 관련 UI 제거
    {
      header: "삭제",
      key: "actions",
    },
  ];

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (
      !window.confirm(
        "정말 이 회원을 삭제하시겠습니까? 삭제 시 복구가 어려울 수 있습니다."
      )
    )
      return;
    const res = await useMemberStore.getState().deleteMember(row.id);
    if (!res.success) {
      alert(`삭제 실패: ${res.error}`);
    }
  };

  const handleCouponCheck = () => {};

  const handleSearch = async () => {
    setSearchTerm(localSearchTerm);
    setSearchField("email");
    await fetchMembers(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title=""
        searchComponent={
          <SearchBar
            placeholder="검색 (이름 또는 이메일을 입력해주세요.)"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        }
        sortComponent={
          <SortDropdown
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
          />
        }
      />

      <ContentContainer>
        {error && (
          <div
            style={{
              background: "#fee",
              color: "#d00",
              padding: 12,
              borderRadius: 8,
              border: "1px solid #fcc",
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>불러오는 중...</div>
        ) : (
          <>
            <Table
              columns={columns}
              data={filteredMembers}
              onDelete={handleDelete}
              onAction={handleCouponCheck}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                marginTop: 16,
              }}
            >
              <Button
                variant="secondary"
                onClick={async () => {
                  if (currentPage > 1) {
                    await fetchMembers(currentPage - 1);
                  }
                }}
                disabled={currentPage <= 1}
              >
                이전
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  await fetchMembers(currentPage + 1);
                }}
              >
                다음
              </Button>
            </div>
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default MemberList;
